import React from 'react';
import {
  FlexColumn,
  FlexRow,
  matchSize,
  Size,
  TruncateBox,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import { DeployContractPackageResult } from '@block-explorer/entities';
import Skeleton from 'react-loading-skeleton';
import { buildRouterPath } from '../../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../../router/paths';
import { HistoryLink } from '../history-link/history-link';
import ContractIcon from '../contract-icon/contract-icon';
import TooltipWithExtendedInfo from '../tooltip-with-extended-info/tooltip-with-extended-info';
import { useTranslation } from 'react-i18next';
import {
  SHORT_CONTRACT_NAME_LENGTH,
  SHORTENED_CONTRACT_NAME_LENGTH,
  truncateName,
} from '../action-messages/utils';

interface TableDataCep18TokenInfoProps {
  data: DeployContractPackageResult | undefined | null;
  loading: boolean;
  symbol?: string;
  nameSize?: Size;
}

const getSize = (size: Size) =>
  matchSize(
    {
      1: SHORTENED_CONTRACT_NAME_LENGTH,
      2: SHORT_CONTRACT_NAME_LENGTH,
    },
    size
  );

export const TableDataCep18TokenInfo = ({
  data,
  loading,
  symbol,
  nameSize = 2,
}: TableDataCep18TokenInfoProps) => {
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <BodyText size={3} scale={'sm'} lineHeight={'sm'}>
        <Skeleton />
      </BodyText>
    );
  }

  const path = buildRouterPath(
    CONTRACT_PACKAGE_PATH,
    { id: data?.contract_package_hash },
    undefined
  );

  const contractName = data.name || data.contract_name;

  const contractTypeId =
    data.contract_type_id || data.latest_version_contract_type_id;

  return (
    <FlexRow align="center" itemsSpacing={12}>
      <ContractIcon
        contractTypeId={contractTypeId}
        src={data.icon_url}
        loading={loading}
      />
      <FlexColumn>
        <>
          <TooltipWithExtendedInfo
            hash={data?.contract_package_hash}
            tooltipCaption={t('Contract Package Hash')}
            extendedLine={{
              title: contractName,
              caption: t('Contract name'),
            }}
          >
            <BodyText size={3} variation="blue" noWrap>
              <HistoryLink
                href={path}
                ariaDescription={'Link to contract package details'}
              >
                {truncateName(contractName, getSize(nameSize))}
              </HistoryLink>
            </BodyText>
          </TooltipWithExtendedInfo>
          <TruncateBox size={nameSize}>
            <BodyText size={3} variation="gray" noWrap>
              {symbol}
            </BodyText>
          </TruncateBox>
        </>
      </FlexColumn>
    </FlexRow>
  );
};
